import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect } from 'react';
import {
  Image,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';

import { usecreatePaygentSettlementMutation } from 'apollo/queries/autogenerate/hooks';
import { usePublicCombiniSettlementQuery } from 'apollo/queries/autogenerate/hooks-public';
import FamimaLogo from 'shared/assets/images/settlements/familymart/logo@3x.png';
import FamimaStep1 from 'shared/assets/images/settlements/familymart/step-1.png';
import FamimaStep2 from 'shared/assets/images/settlements/familymart/step-2.png';
import FamimaStep3 from 'shared/assets/images/settlements/familymart/step-3.png';
import FamimaStep4 from 'shared/assets/images/settlements/familymart/step-4.png';
import FamimaStep5 from 'shared/assets/images/settlements/familymart/step-5.png';
import FamimaStep6 from 'shared/assets/images/settlements/familymart/step-6.png';
import FamimaStep7 from 'shared/assets/images/settlements/familymart/step-7.png';
import FamimaStep8 from 'shared/assets/images/settlements/familymart/step-8.png';
import LogoLawson from 'shared/assets/images/settlements/loppi/logo-lawson@3x.png';
import LogoMinistop from 'shared/assets/images/settlements/loppi/logo-ministop@3x.png';
import LoppiStep1 from 'shared/assets/images/settlements/loppi/step-1.png';
import LoppiStep2 from 'shared/assets/images/settlements/loppi/step-2.png';
import LoppiStep3 from 'shared/assets/images/settlements/loppi/step-3.png';
import LoppiStep4 from 'shared/assets/images/settlements/loppi/step-4.png';
import LoppiStep5 from 'shared/assets/images/settlements/loppi/step-5.png';
import LogoSeicomart from 'shared/assets/images/settlements/seicomart/logo@3x.png';
import SeicomartStep1 from 'shared/assets/images/settlements/seicomart/step-1.png';
import SeicomartStep2 from 'shared/assets/images/settlements/seicomart/step-2.png';
import SeicomartStep3 from 'shared/assets/images/settlements/seicomart/step-3.png';
import SeicomartStep4 from 'shared/assets/images/settlements/seicomart/step-4.png';
import SeicomartStep5 from 'shared/assets/images/settlements/seicomart/step-5.png';
import SeicomartStep6 from 'shared/assets/images/settlements/seicomart/step-6.png';
import generateID from 'shared/modules/common/utils/generate-id';
import { formatToCurrency } from 'shared/utils/japanese';

type Props = {
  billIdOrPaidyCode: string;
  combini: 'familymart' | 'loppi' | 'seicomart';
  onError?: (error: Error) => void;
  showTitle?: boolean;
  style?: StyleProp<ViewStyle>;
};

const combiniData = {
  familymart: {
    title: 'ファミリーマート',
    subtitle: 'ファミリーマート',
    logos: [FamimaLogo],
    instructions: [
      {
        image: FamimaStep1,
        description: '① 「代金支払い」をタッチ',
      },
      {
        image: FamimaStep2,
        description: '② 出力物をご確認',
      },
      {
        image: FamimaStep3,
        description: '③ 「番号入力へ」をタッチ',
      },
      {
        image: FamimaStep4,
        description: '④ 収納番号の左5桁「20020」を入力し「OK」をタッチ',
      },
      {
        image: FamimaStep5,
        description: '⑤ 「収納番号の右12桁」を入力し「OK」をタッチ',
      },
      {
        image: FamimaStep6,
        description: '⑥ 内容を確認し「同意して進む」をタッチ',
      },
      {
        image: FamimaStep7,
        description: '⑦ 内容を確認し「確認」をタッチ',
      },
      {
        image: FamimaStep8,
        description:
          '⑧ 出力された申込券をレジにて提示\n\nマルチコピー機から「Mコピーサービス申込券」が発行されますので、お支払い有効期限までにレジにてお支払いください。',
      },
    ],
  },
  loppi: {
    title: 'ローソンまたはミニストップ',
    subtitle: 'ローソン・ミニストップ',
    logos: [LogoLawson, LogoMinistop],
    instructions: [
      {
        image: LoppiStep1,
        description:
          'トップ画面左の「各種番号をお持ちの方」ボタンを押してください。',
      },
      {
        image: LoppiStep2,
        description: '「お支払い受付番号(#_merchantCode_#)」を入力',
      },
      {
        image: LoppiStep3,
        description: 'お客様の電話番号(#_consumerCode_#)を入力',
      },
      {
        image: LoppiStep4,
        description: '内容をご確認後、「了解」のボタンを押してください。',
      },
      {
        image: LoppiStep5,
        description:
          '「申込券」の出力、レジにてお支払い Loppi端末より「申込券」が出力されますので、「申込券」を持って30分以内にレジにて代金をお支払いください。\n\n※代金と引き換えに「領収書」をお渡ししますので、必ずお受取りください。',
      },
    ],
  },
  seicomart: {
    title: 'セイコーマート',
    subtitle: 'セイコーマート',
    logos: [LogoSeicomart],
    instructions: [
      {
        image: SeicomartStep1,
        description: '受付番号をお控えください。',
      },
      {
        image: SeicomartStep2,
        description:
          'セイコーマート店頭のレジにて「インターネット支払い」とお伝えください。',
      },
      {
        image: SeicomartStep3,
        description:
          'レジ操作画面に受付番号（#_merchantCode_#）をハイフン（-）を除いて入力し「登録」を選択。',
      },
      {
        image: SeicomartStep4,
        description:
          'レジ操作画面にお客様の電話番号（#_consumerCode_#）を入力し「登録」を選択。',
      },
      {
        image: SeicomartStep5,
        description: '確認画面が表示されます。宜しければ「OK」ボタンを選択。',
      },
      {
        image: SeicomartStep6,
        description: '代金をお支払いください。',
      },
    ],
  },
};

const CombiniSettlement = (props: Props) => {
  const isUsingPaidyCode = props.billIdOrPaidyCode.length === 10;
  const isFamilyMart = props.combini === 'familymart';

  // Only used when authenticated
  const [
    createPaygentMutation,
    { data: createPaygentSettlementData },
  ] = usecreatePaygentSettlementMutation({
    fetchPolicy: 'no-cache',
    onError: props.onError,
  });

  useEffect(() => {
    if (!isUsingPaidyCode) {
      createPaygentMutation({
        variables: {
          billId: props.billIdOrPaidyCode,
          familyMart: isFamilyMart,
        },
      }).catch(props.onError);
    }
  }, [props.billIdOrPaidyCode, props.combini]);

  // Only used when using the non-authenticated settlement
  const { data: publicCombiniSettlementData } = usePublicCombiniSettlementQuery(
    {
      fetchPolicy: 'network-only',
      context: {
        uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
      },
      skip: !isUsingPaidyCode,
      variables: {
        paidyCode: props.billIdOrPaidyCode,
        isFamilyMart,
      },
    }
  );

  // Both have the same Paygent Settlement object
  const data =
    createPaygentSettlementData?.createPaygentSettlement ||
    publicCombiniSettlementData?.combiniSettlement;

  if (!data) {
    return null;
  }

  return (
    <View style={[style.root, props.style]}>
      {props.showTitle && (
        <Text style={[style.textBold, style.textCenter, style.marginBottom32]}>
          {combiniData[props.combini].title}
        </Text>
      )}
      <Text
        style={[
          style.textSmall,
          style.textBold,
          style.textCenter,
          style.marginBottom16,
        ]}
      >
        {`${combiniData[props.combini].title}のお支払い方法`}
      </Text>
      <View style={style.logosWrapper}>
        {combiniData[props.combini].logos.map((logo, index) => (
          <Image
            key={index}
            source={{ uri: logo }}
            style={[style.logo, style.marginBottom16]}
          />
        ))}
      </View>
      <Text
        style={[
          style.textSmall,
          style.textCenter,
          style.textSecondary,
          style.marginBottom16,
        ]}
      >
        {combiniData[props.combini].subtitle}
      </Text>
      <LinearGradient
        colors={['#E5258C', '#823CD7']}
        /** will do an approximately 135-degree gradient */
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 6 }}
        style={{ paddingVertical: 16, paddingHorizontal: 24 }}
      >
        <Text style={[style.text, style.textBold]}>お支払い金額</Text>
        <Text
          style={[style.text, style.textBold, style.textHighlight]}
          {...generateID('combini-settlement-detail-amount')}
        >
          {formatToCurrency(data.amount + (data.settlementFeeAmount || 0))}
        </Text>
        <Text style={style.text}>
          コンビニ支払手数料
          {formatToCurrency(data.settlementFeeAmount || 0)}
          を含んでいます
        </Text>
        <View style={style.separator} />
        <Text style={[style.text, style.textBold]}>お支払い受付番号</Text>
        <Text
          style={[style.text, style.textBold, style.textHighlight]}
          {...generateID('combini-settlement-detail-merchant-code')}
        >
          {data.codes.merchantCode}
        </Text>
        {props.combini === 'familymart' && (
          <Text style={style.text}>
            この番号はファミポート以外では使用できません
          </Text>
        )}
      </LinearGradient>
      <View style={{ padding: 24 }}>
        {combiniData[props.combini].instructions.map((step, index) => (
          <View
            key={index}
            style={
              index < combiniData[props.combini].instructions.length - 1 &&
              style.marginBottom32
            }
          >
            <Image
              source={{ uri: step.image }}
              style={style.instructionImage}
            />
            <Text
              style={[style.textBold, style.textSecondary, style.textSmall]}
            >
              {step.description
                .replace('#_merchantCode_#', data.codes.merchantCode)
                .replace('#_consumerCode_#', data.codes.consumerCode)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    backgroundColor: 'white',
    padding: 24,
    alignItems: 'stretch',
  },
  textBold: {
    fontWeight: 'bold',
  },
  textSmall: {
    fontSize: 12,
  },
  textSecondary: {
    color: '#586065',
  },
  textCenter: {
    textAlign: 'center',
  },
  marginBottom16: {
    marginBottom: 16,
  },
  marginBottom32: {
    marginBottom: 32,
  },
  logosWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  logo: {
    width: 56,
    height: 56,
    marginHorizontal: 8,
  },
  instructionImage: {
    width: '100%',
    height: 320,
    resizeMode: 'contain',
    marginBottom: 8,
  },
  text: {
    color: 'white',
    marginBottom: 16,
    fontSize: 12,
  },
  textHighlight: {
    fontSize: 24,
  },
  separator: {
    backgroundColor: 'white',
    marginBottom: 16,
    height: StyleSheet.hairlineWidth,
  },
});

export default CombiniSettlement;
export { combiniData };
